import type { FC } from "react";
import { useCallback, useMemo } from "react";
import { Box, Container, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import ApartmentOutlined from "@mui/icons-material/ApartmentOutlined";
import CategoryOutlined from "@mui/icons-material/CategoryOutlined";
import ChevronRightOutlined from "@mui/icons-material/ChevronRightOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import SellOutlined from "@mui/icons-material/SellOutlined";
import ShoppingBasketOutlined from "@mui/icons-material/ShoppingBasketOutlined";
import SourceOutlined from "@mui/icons-material/SourceOutlined";
import SpaOutlined from "@mui/icons-material/SpaOutlined";
import TopicOutlined from "@mui/icons-material/TopicOutlined";
import type { SvgIconComponent } from "@mui/icons-material";
import { WhiteBox } from "@foodpilot/foods";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { URLHandler } from "src/services/URLHandler";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import {
    BRAND,
    COHORT_DEFINITION,
    ORGANIZATION,
    COMPANY_SECTOR,
    CREATE,
    DELETE,
    INGREDIENT_LINE,
    MODIFY,
    PRODUCT_SETTINGS_ORGANIZATION,
    PRODUCT_LINE,
    TAG_TYPE,
    useFoodpilotContext,
    VIEW,
} from "src/context/FoodpilotContext";
import { SettingsCard, SETTINGS_CARD_MUI_ICON_SIZE } from "./components/SettingsCard";
import { SettingsPageHeader } from "./components/SettingsPageHeader";
import { filterVisibleSections } from "./filterVisibleSections";
import { canAdministerSuppliers } from "./Supplier/supplierAdministration";

type OrganizationIcon = SvgIconComponent;

type OrganizationItem = {
    title: string;
    description: string;
    icon: OrganizationIcon;
    onClick: () => void;
    isVisible: boolean;
};

type OrganizationSection = {
    id: string;
    title: string;
    subtitle: string;
    icon: OrganizationIcon;
    homePath?: string;
    items: OrganizationItem[];
};

const ROW_MUI_ICON_SIZE = 22;

const renderOrganizationIcon = (Icon: OrganizationIcon, size: number) => <Icon sx={{ fontSize: size }} />;

export const MyOrganization: FC = () => {
    const navigate = useNavigate();
    const { sectionId } = useParams();
    const { t } = useTranslation();
    const { canUser, isAdmin, user } = useFoodpilotContext();
    const { isMobile } = useResponsiveBreakpoints();
    const theme = useTheme();

    const canViewOrganizationAdministration = canUser(VIEW, ORGANIZATION);
    const canViewSupplierAdministration = canAdministerSuppliers(user.trustedUser, VIEW);
    const canManageCompanySectors =
        canUser(CREATE, COMPANY_SECTOR) || canUser(MODIFY, COMPANY_SECTOR) || canUser(DELETE, COMPANY_SECTOR);
    const canManageBrands = canUser(CREATE, BRAND) || canUser(MODIFY, BRAND) || canUser(DELETE, BRAND);
    const canManageProductLines =
        canUser(CREATE, PRODUCT_LINE) || canUser(MODIFY, PRODUCT_LINE) || canUser(DELETE, PRODUCT_LINE);
    const canManageIngredientLines =
        canUser(CREATE, INGREDIENT_LINE) || canUser(MODIFY, INGREDIENT_LINE) || canUser(DELETE, INGREDIENT_LINE);
    const canManagePackaging = canUser(MODIFY, PRODUCT_SETTINGS_ORGANIZATION);
    const canManageTags = canUser(CREATE, TAG_TYPE) || canUser(MODIFY, TAG_TYPE) || canUser(DELETE, TAG_TYPE);
    const canManageCustomAttributes = isAdmin();
    const canViewCohortDefinitions = canUser(VIEW, COHORT_DEFINITION);

    const organizationSections: OrganizationSection[] = useMemo(
        () => [
            {
                id: "companies-suppliers",
                title: t("settings.organization.companies_suppliers.title", "Companies & suppliers"),
                subtitle: t(
                    "settings.organization.companies_suppliers.subtitle",
                    "Companies, organization types, industry sectors, and suppliers",
                ),
                icon: ApartmentOutlined,
                items: [
                    {
                        title: t("settings.organization.companies_suppliers.companies.title", "Companies"),
                        description: t(
                            "settings.organization.companies_suppliers.companies.description",
                            "Manage client companies and their organization data.",
                        ),
                        icon: ApartmentOutlined,
                        onClick: () =>
                            navigate(URLHandler.settings.myOrganizationPath("companies-suppliers", "companies")),
                        isVisible: canViewOrganizationAdministration,
                    },
                    {
                        title: t(
                            "settings.organization.companies_suppliers.organization_types.title",
                            "Organization types",
                        ),
                        description: t(
                            "settings.organization.companies_suppliers.organization_types.description",
                            "Define the organization types available for companies.",
                        ),
                        icon: AccountTreeOutlined,
                        onClick: () =>
                            navigate(
                                URLHandler.settings.myOrganizationPath("companies-suppliers", "organization-types"),
                            ),
                        isVisible: isAdmin(),
                    },
                    {
                        title: t(
                            "settings.organization.companies_suppliers.industry_sectors.title",
                            "Industry sectors",
                        ),
                        description: t(
                            "settings.organization.companies_suppliers.industry_sectors.description",
                            "Maintain the sectors used to classify companies.",
                        ),
                        icon: CategoryOutlined,
                        onClick: () =>
                            navigate(URLHandler.settings.myOrganizationPath("companies-suppliers", "industry-sectors")),
                        isVisible: canManageCompanySectors,
                    },
                    {
                        title: t("settings.organization.companies_suppliers.suppliers.title", "Suppliers"),
                        description: t(
                            "settings.organization.companies_suppliers.suppliers.description",
                            "Manage supplier organizations linked to the client.",
                        ),
                        icon: LocalShippingOutlined,
                        onClick: () =>
                            navigate(URLHandler.settings.myOrganizationPath("companies-suppliers", "suppliers")),
                        isVisible: canViewSupplierAdministration,
                    },
                ],
            },
            {
                id: "products-ingredients",
                title: t("settings.organization.products_ingredients.title", "Products & ingredients"),
                subtitle: t(
                    "settings.organization.products_ingredients.subtitle",
                    "Brands, product lines, and ingredient sectors",
                ),
                icon: ShoppingBasketOutlined,
                items: [
                    {
                        title: t("settings.organization.products_ingredients.brands.title", "Brands"),
                        description: t(
                            "settings.organization.products_ingredients.brands.description",
                            "Manage the brands used to organize products.",
                        ),
                        icon: LocalOfferOutlined,
                        onClick: () =>
                            navigate(URLHandler.settings.myOrganizationPath("products-ingredients", "brands")),
                        isVisible: canManageBrands,
                    },
                    {
                        title: t("settings.organization.products_ingredients.product_lines.title", "Product lines"),
                        description: t(
                            "settings.organization.products_ingredients.product_lines.description",
                            "Maintain product line hierarchies and defaults.",
                        ),
                        icon: ShoppingBasketOutlined,
                        onClick: () =>
                            navigate(URLHandler.settings.myOrganizationPath("products-ingredients", "product-lines")),
                        isVisible: canManageProductLines,
                    },
                    {
                        title: t(
                            "settings.organization.products_ingredients.ingredient_sectors.title",
                            "Ingredient sectors",
                        ),
                        description: t(
                            "settings.organization.products_ingredients.ingredient_sectors.description",
                            "Maintain ingredient sector hierarchies.",
                        ),
                        icon: SpaOutlined,
                        onClick: () =>
                            navigate(
                                URLHandler.settings.myOrganizationPath("products-ingredients", "ingredient-sectors"),
                            ),
                        isVisible: canManageIngredientLines,
                    },
                ],
            },
            {
                id: "packaging",
                title: t("settings.organization.packaging.title", "Packaging"),
                subtitle: t("settings.organization.packaging.subtitle", "Packaging configuration and QSS assignment"),
                icon: Inventory2Outlined,
                items: [
                    {
                        title: t("settings.organization.packaging.packaging_qss.title", "Packaging QSS"),
                        description: t(
                            "settings.organization.packaging.packaging_qss.description",
                            "Choose the questionnaire used for packaging assessments.",
                        ),
                        icon: TopicOutlined,
                        onClick: () => navigate(URLHandler.settings.myOrganizationPath("packaging", "packaging-qss")),
                        isVisible: canManagePackaging,
                    },
                ],
            },
            {
                id: "custom-data-taxonomies",
                title: t("settings.organization.custom_data_taxonomies.title", "Custom data & taxonomies"),
                subtitle: t(
                    "settings.organization.custom_data_taxonomies.subtitle",
                    "Custom attributes, cohorts, and tags",
                ),
                icon: SourceOutlined,
                items: [
                    {
                        title: t(
                            "settings.organization.custom_data_taxonomies.custom_attributes.title",
                            "Custom attributes",
                        ),
                        description: t(
                            "settings.organization.custom_data_taxonomies.custom_attributes.description",
                            "Configure custom data fields for organization entities.",
                        ),
                        icon: SourceOutlined,
                        onClick: () =>
                            navigate(
                                URLHandler.settings.myOrganizationPath("custom-data-taxonomies", "custom-attributes"),
                            ),
                        isVisible: canManageCustomAttributes,
                    },
                    {
                        title: t(
                            "settings.organization.custom_data_taxonomies.cohort_definitions.title",
                            "Cohort definitions",
                        ),
                        description: t(
                            "settings.organization.custom_data_taxonomies.cohort_definitions.description",
                            "Group peers that share an attribute, such as a delegation.",
                        ),
                        icon: GroupsOutlined,
                        onClick: () =>
                            navigate(
                                URLHandler.settings.myOrganizationPath("custom-data-taxonomies", "cohort-definitions"),
                            ),
                        isVisible: canViewCohortDefinitions,
                    },
                    {
                        title: t("settings.organization.custom_data_taxonomies.tags.title", "Tags"),
                        description: t(
                            "settings.organization.custom_data_taxonomies.tags.description",
                            "Manage tag types and tag values used across entities.",
                        ),
                        icon: SellOutlined,
                        onClick: () =>
                            navigate(URLHandler.settings.myOrganizationPath("custom-data-taxonomies", "tags")),
                        isVisible: canManageTags,
                    },
                ],
            },
        ],
        [
            canManageBrands,
            canViewOrganizationAdministration,
            canViewSupplierAdministration,
            canManageCompanySectors,
            canManageCustomAttributes,
            canViewCohortDefinitions,
            canManageIngredientLines,
            canManagePackaging,
            canManageProductLines,
            canManageTags,
            isAdmin,
            navigate,
            t,
        ],
    );

    const visibleOrganizationSections = useMemo(
        () => filterVisibleSections(organizationSections),
        [organizationSections],
    );

    const selectedSection = useMemo(
        () => visibleOrganizationSections.find((section) => section.id === sectionId),
        [visibleOrganizationSections, sectionId],
    );

    const selectedSectionBorder = useCallback(
        (index: number) =>
            selectedSection && index === selectedSection.items.length - 1 ? 0 : `1px solid ${theme.custom.grey[500]}`,
        [selectedSection, theme.custom.grey],
    );

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                m: "0",
                p: isMobile ? theme.spacing(1) : `${theme.spacing(6)} !important`,
                flexGrow: 1,
                overflow: "auto",
            }}
        >
            {selectedSection ?
                <Box>
                    <SettingsPageHeader
                        title={selectedSection.title}
                        subtitle={selectedSection.subtitle}
                        breadcrumbs={[
                            {
                                label: t("settings.organization.title", "Organization"),
                                to: URLHandler.settings.myOrganization(),
                            },
                            { label: selectedSection.title },
                        ]}
                    />

                    <WhiteBox
                        boxShadow="none"
                        borderRadius={theme.spacing(1)}
                        sx={{
                            width: "100%",
                            overflow: "hidden",
                            backgroundColor: theme.custom.grey[100],
                        }}
                    >
                        {selectedSection.items.map((item, index) => (
                            <Box
                                key={item.title}
                                component="button"
                                type="button"
                                onClick={item.onClick}
                                sx={{
                                    width: "100%",
                                    p: theme.spacing(2.5, 3),
                                    border: 0,
                                    borderBottom: selectedSectionBorder(index),
                                    cursor: "pointer",
                                    backgroundColor: theme.custom.grey[100],
                                    fontFamily: "inherit",
                                    textAlign: "left",
                                    transition: "background-color 0.2s ease",
                                    "&:hover": {
                                        backgroundColor: theme.custom.grey[300],
                                    },
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={theme.spacing(2.5)}>
                                    <Box
                                        sx={{
                                            width: theme.spacing(5),
                                            height: theme.spacing(5),
                                            borderRadius: theme.spacing(1),
                                            backgroundColor: theme.custom.grey[300],
                                            color: theme.palette.primary[600],
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {renderOrganizationIcon(item.icon, ROW_MUI_ICON_SIZE)}
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            variant="body-medium"
                                            sx={{ color: theme.custom.grey[2800], display: "block" }}
                                        >
                                            {item.title}
                                        </Typography>
                                        <Typography
                                            variant="body"
                                            sx={{
                                                color: theme.custom.grey[2100],
                                                display: "block",
                                                mt: theme.spacing(0.5),
                                            }}
                                        >
                                            {item.description}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ color: theme.custom.grey[2800], flexShrink: 0 }}>
                                        <ChevronRightOutlined fontSize="small" />
                                    </Box>
                                </Stack>
                            </Box>
                        ))}
                    </WhiteBox>
                </Box>
            :   <Box>
                    <Typography variant="h3" sx={{ color: theme.custom.grey[2800], mb: theme.spacing(3) }}>
                        {t("settings.organization.title", "Organization")}
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                            gap: theme.spacing(3),
                        }}
                    >
                        {visibleOrganizationSections.map((section) => (
                            <SettingsCard
                                key={`organization-section-${section.id}`}
                                title={section.title}
                                description={section.subtitle}
                                icon={renderOrganizationIcon(section.icon, SETTINGS_CARD_MUI_ICON_SIZE)}
                                to={section.homePath ?? URLHandler.settings.myOrganizationSection(section.id)}
                            />
                        ))}
                    </Box>
                </Box>
            }
        </Container>
    );
};
